import { removeEmptyNullAndUndefinedFromObject } from "../common";
import {
  ADDRESS,
  excludePincodeCountries,
  specialCountries,
} from "../../components/organisms/daSectionTiles/addressForm/receiverAddress/constant";
import { THIS_IS_REQUIRED } from "../../constants/pageConstants/deliveryAddressPageConstant";
import { isReceiverEmailRequired } from "../checkout/deliveryAddress";
import { buildRecipientUpdatePayload } from "./orderModificationHelpers";
import Regex from "../regex";

export const ACCOUNT_DEFAULT_ADDRESS_STATE = {
  addressPurposeType: "",
  postalCode: "",
  teleCode: "91",
  altRecpTeleCode: "91",
  city: "",
  countryGeoId: "IND",
  addressType: "other",
  selectedContactMechId: ADDRESS.NEW_ADDRESS,
};

export const ACCOUNT_ADDRESS_FORM_VARIANT = {
  DOMESTIC: "domestic",
  SPECIAL: "special",
  INTERNATIONAL: "international",
};

/**
 * @param {string} countryGeoId
 * @returns {string}
 */
export function normalizeCountryGeoId(countryGeoId) {
  const normalized = String(countryGeoId || "IND")
    .trim()
    .toUpperCase();
  return normalized || "IND";
}

/**
 * India → domestic; USA/CAN/GBR/IRL (or API special list) → special; all other countries → international DA form.
 *
 * @param {string} countryGeoId
 * @param {string[]} specialCountryCodes
 * @returns {typeof ACCOUNT_ADDRESS_FORM_VARIANT[keyof typeof ACCOUNT_ADDRESS_FORM_VARIANT]}
 */
export function resolveAccountAddressFormVariant(countryGeoId, specialCountryCodes = specialCountries) {
  const id = normalizeCountryGeoId(countryGeoId);
  if (id === "IND") {
    return ACCOUNT_ADDRESS_FORM_VARIANT.DOMESTIC;
  }
  const specialSet = new Set(
    (specialCountryCodes || specialCountries).map((code) => normalizeCountryGeoId(code)).filter(Boolean),
  );
  if (specialSet.has(id)) {
    return ACCOUNT_ADDRESS_FORM_VARIANT.SPECIAL;
  }
  return ACCOUNT_ADDRESS_FORM_VARIANT.INTERNATIONAL;
}

/**
 * Doorbell is collected for special-country forms (and DEU at checkout).
 *
 * @param {string} countryGeoId
 * @param {string[]} [specialCountryCodes]
 * @returns {boolean}
 */
export function isDoorBellSupportedCountry(countryGeoId, specialCountryCodes = specialCountries) {
  const id = normalizeCountryGeoId(countryGeoId);
  if (id === "DEU") {
    return true;
  }
  return resolveAccountAddressFormVariant(id, specialCountryCodes) === ACCOUNT_ADDRESS_FORM_VARIANT.SPECIAL;
}

/**
 * @param {object} form
 * @param {object} item
 * @param {string} countryGeoId
 * @returns {object}
 */
function applyInternationalAddressFields(form, item, countryGeoId) {
  if (!countryGeoId || countryGeoId === "IND") {
    return {
      ...form,
      address2: item?.address2 || item?.localityArea || form.address2 || "",
    };
  }

  const zip = item?.intlpostalcode || item?.address2 || item?.localityArea || "";
  const next = {
    ...form,
    address2: zip,
    addressLine2: item?.addressLine2 || item?.postalCodeExt || "",
    address3: item?.address3 || item?.houseNo || "",
    houseNo: item?.houseNo || item?.address3 || "",
    doorBellName: item?.doorBell || item?.doorBellName || form.doorBellName || "",
  };

  if (specialCountries.includes(countryGeoId) && item?.addressId) {
    next.addressId = item.addressId;
  }

  return next;
}

/**
 * @param {object} item
 * @returns {object}
 */
export function mapAccountListItemToForm(item) {
  if (!item) {
    return { ...ACCOUNT_DEFAULT_ADDRESS_STATE };
  }
  const countryGeoId = normalizeCountryGeoId(item?.countryGeoId);
  const base = {
    ...ACCOUNT_DEFAULT_ADDRESS_STATE,
    salutation: item?.salutation || "MR",
    toName: item?.name || item?.toName || "",
    email: item?.email || item?.attnName || "",
    address1: item?.address || item?.address1 || "",
    city: item?.city || "",
    postalCode: item?.postalCode || item?.postalcode || "",
    mobileNumber: item?.mobileNumber || item?.phone || "",
    altRecpNumber: item?.altMobileNumber || item?.altRecpNumber || "",
    contactMechId: item?.id || item?.contactMechId || "",
    postalAddress: item?.id || item?.postalAddress || "",
    countryGeoId,
    addressType: item?.addressType || "other",
    teleCode: item?.mobileCode || item?.teleCode || "91",
    landmark: item?.landmark || "",
    flatHouseNo: item?.flatHouseNo || "",
    directions: item?.directions || item?.areaLocality || "",
    doorBellName: item?.doorBell || item?.doorBellName || "",
    selectedContactMechId: item?.id || ADDRESS.EDIT_ADDRESS,
  };
  return applyInternationalAddressFields(base, item, countryGeoId);
}

/**
 * @param {object} recipientDetails
 * @returns {object}
 */
export function mapRecipientDetailsToAddressForm(recipientDetails) {
  if (!recipientDetails) {
    return { ...ACCOUNT_DEFAULT_ADDRESS_STATE };
  }
  const countryGeoId = normalizeCountryGeoId(recipientDetails.recipientCountryGeoId);
  const base = {
    ...ACCOUNT_DEFAULT_ADDRESS_STATE,
    toName: recipientDetails.recipientName || "",
    email: recipientDetails.recipientEmail || "",
    address1: recipientDetails.recipientAddress1 || recipientDetails.recipientAddress || "",
    city: recipientDetails.recipientCity || "",
    postalCode: recipientDetails.recipientPostalCode || "",
    mobileNumber: recipientDetails.recipientContactNumber || "",
    altRecpNumber: recipientDetails.recipientAltContactNumber || "",
    countryGeoId,
    landmark: recipientDetails.landmark || "",
    flatHouseNo: recipientDetails.flatHouseNo || "",
    directions: recipientDetails.directions || "",
    addressType: recipientDetails.addressType || "home",
    salutation: recipientDetails.salutation || "",
    selectedContactMechId: ADDRESS.EDIT_ADDRESS,
  };
  return applyInternationalAddressFields(
    base,
    {
      address2: recipientDetails.recipientAddress2,
      localityArea: recipientDetails.localityArea,
      intlpostalcode: recipientDetails.intlpostalcode,
      houseNo: recipientDetails.houseNo,
      doorBell: recipientDetails.doorBell,
    },
    countryGeoId,
  );
}

/**
 * @param {object} addressObj
 * @param {boolean} isNewAddress
 * @param {string} selectedCountryId
 * @returns {object}
 */
export function buildUpdateAddressRjPayload(
  addressObj,
  isNewAddress,
  selectedCountryId = "IND",
  specialCountryCodes = specialCountries,
) {
  let payload = {
    addrSalutation: addressObj?.salutation || "",
    toName: addressObj?.toName || "",
    attnName: addressObj?.email || "",
    address: addressObj?.address1 || "",
    address2: addressObj?.address2 || "",
    landmark: addressObj?.landmark || "",
    postalcode: addressObj?.postalCode || "",
    city: addressObj?.city || "",
    phone: addressObj?.mobileNumber || "",
    postalAddress: isNewAddress ? "noPartyPostal" : addressObj?.postalAddress || addressObj?.contactMechId || "",
    countryGeoId: addressObj?.countryGeoId || selectedCountryId || "IND",
    countryCode: addressObj?.teleCode || "91",
    telecomPurposeId: "PHONE_SHIPPING",
    contactMechPurpse: "SHIPPING_LOCATION",
    addressType: addressObj?.addressType || "other",
    stateProvinceGeoId: "_NA_",
    altTelecomPurposeId: "PHONE_SHIPPING",
    altPhone: addressObj?.altRecpNumber || "",
    latitude: addressObj?.latitude || "",
    longitude: addressObj?.longitude || "",
    isOrderingForSelf: addressObj?.isOrderingForSelf ? "Y" : "N",
    flatHouseNo: addressObj?.flatHouseNo || "",
    directions: addressObj?.directions || "",
    doorBellName: addressObj?.doorBellName || addressObj?.doorBell || "",
    houseNo: addressObj?.houseNo || "",
  };
  if (selectedCountryId !== "IND" && !specialCountryCodes.includes(selectedCountryId)) {
    payload.address2 = addressObj?.addressLine2 || "";
    payload.intlpostalcode = addressObj?.address2 || "";
  } else if (selectedCountryId !== "IND" && specialCountryCodes.includes(selectedCountryId)) {
    payload.address2 = "";
    payload.houseNo = addressObj?.address3 || addressObj?.houseNo || "";
    payload.doorBellName = addressObj?.doorBellName || addressObj?.doorBell || "";
    payload.intlpostalcode = addressObj?.address2 || "";
    if (addressObj?.addressId) {
      payload.addressId = addressObj.addressId;
    }
  }
  if (selectedCountryId !== "IND") {
    payload.intlpostalcode = payload.intlpostalcode || addressObj?.address2 || "";
  }
  return removeEmptyNullAndUndefinedFromObject(payload);
}

/**
 * @param {object} addressObj
 * @param {string} countryGeoId
 * @returns {{ valid: boolean, next: object }}
 */
export function validateAccountAddressForm(addressObj, countryGeoId = "IND", catalogId = "") {
  const next = { ...addressObj };
  const required = [ADDRESS.ADDRESS_1, ADDRESS.TO_NAME, ADDRESS.MOBILE_NUMBER];
  if (!excludePincodeCountries.includes(countryGeoId)) {
    required.push(ADDRESS.ADDRESS_2);
  }
  if (isReceiverEmailRequired({ catalogId, countryId: countryGeoId })) {
    required.push(ADDRESS.EMAIL);
  }
  if (countryGeoId === "IND") {
    required.push(ADDRESS.POSTAL_CODE);
  } else {
    required.push(ADDRESS.CITY, ADDRESS.POSTAL_CODE);
  }

  let valid = true;
  required.forEach((field) => {
    const errorKey = `${field}Error`;
    const value = next[field];
    const isEmpty = value === undefined || value === null || String(value).trim() === "";
    if (isEmpty || next[errorKey]) {
      next[errorKey] = true;
      next[`${field}ErrorMsg`] = THIS_IS_REQUIRED;
      valid = false;
    } else {
      next[errorKey] = false;
      next[`${field}ErrorMsg`] = "";
    }
  });

  if (next[ADDRESS.EMAIL] && Regex.checkEmail && !Regex.checkEmail.test(String(next[ADDRESS.EMAIL]).trim())) {
    next[ADDRESS.EMAIL_ERROR] = true;
    next[ADDRESS.EMAIL_ERROR_MSG] = "Please enter valid email";
    valid = false;
  }

  const mobile = String(next[ADDRESS.MOBILE_NUMBER] || "");
  const mobileValid = countryGeoId === "IND" ? mobile.length === 10 : mobile.length >= 7 && mobile.length <= 15;
  if (!mobileValid) {
    next[ADDRESS.MOBILE_NUMBER_ERROR] = true;
    next[ADDRESS.MOBILE_NUMBER_ERROR_MSG] = "Please enter valid number";
    valid = false;
  }

  return { valid, next };
}

/**
 * @param {object} formAddress
 * @param {string} subOrderId
 * @returns {Record<string, string>}
 */
export function buildOrderRecipientPayloadFromForm(formAddress, subOrderId) {
  return buildRecipientUpdatePayload(
    {
      name: formAddress.toName,
      mobileNumber: formAddress.mobileNumber,
      altMobileNumber: formAddress.altRecpNumber,
      email: formAddress.email,
      address: formAddress.address1,
      localityArea: formAddress.address2,
      postalCode: formAddress.postalCode,
      city: formAddress.city,
      countryGeoId: formAddress.countryGeoId,
      landmark: formAddress.landmark,
      houseNo: formAddress.houseNo,
      flatHouseNo: formAddress.flatHouseNo,
      directions: formAddress.directions,
      doorBell: formAddress.doorBellName,
      addressType: formAddress.addressType,
      salutation: formAddress.salutation,
    },
    subOrderId,
  );
}
